/**
 * SCRIPT mode, Welsh — THE MIXED ORDER: half course, half out and about (job #149).
 *
 * Job #145 re-ordered the Welsh deck by how useful each item is to read out
 * and about against what it costs the learner, and offered three arms; Watson
 * chose the BLEND overnight under Tom's authority (job #149, reversible on
 * staging): a word's usefulness is half its out-and-about score
 * (tools/script-corpus/cym/out/out-and-about.<variety>.tsv, its mutation-family
 * column) and half the course's own word counts (its seeds and pod lines),
 * both per million. It brings street Welsh forward and accepts fewer early
 * full sentences. This file is #145's order.py, ported line for line so the
 * app's build reproduces the order that was measured.
 *
 *   USEFULNESS of a chunk  its RAREST word's: a chunk can be no commoner than
 *                          its rarest word
 *   USEFULNESS of an item  the same over all its words
 *   LOAD of an item        the chunks it opens the learner has not met yet
 *
 * ONE NEW THING AT A TIME: at each step every item opening nothing new joins
 * quietly, most useful first; then the item opening exactly one new chunk
 * with the biggest GAIN goes next — the chunk's own usefulness plus UNLOCK of
 * the usefulness of every line it would make fully readable, so the order
 * keeps reaching sentences and street items rather than drilling bare LEGOs.
 * A LEGO goes before a line carrying the same chunk; the incoming order breaks
 * any tie. Only when nothing opens a single chunk does an item opening more
 * come in, the best usefulness per new chunk first.
 *
 * Pure: items and a word table in, items out, nothing dropped or changed.
 */
import { wordsOf, type WelshItem, type WelshItemKind } from './welshDeck'

/** Per million, for a word no source has seen. */
export const FLOOR = 0.1
/** The share of a completed line's usefulness its last chunk earns. */
export const UNLOCK = 0.5
/** The out-and-about half and the course half of the blend. */
export const STREET_SHARE = 0.5

const KIND_RANK: Record<WelshItemKind, number> = { L: 0, S: 1, U: 2, P: 3, O: 4 }

/** Mutated onset → radical onset: soft, nasal, aspirate. Mechanical and over-generous, as #145 ruled it approximate. */
const UNMUTATE: readonly [string, string][] = [
  ['ngh', 'c'], ['mh', 'p'], ['nh', 't'], ['ng', 'g'], ['ph', 'p'], ['th', 't'], ['ch', 'c'], ['dd', 'd'],
  ['b', 'p'], ['d', 't'], ['g', 'c'], ['f', 'b'], ['f', 'm'], ['l', 'll'], ['r', 'rh'], ['m', 'b'], ['n', 'd'],
]
const VOWELS = 'aeiouwyâêîôûŵŷ'

/** Every radical a Welsh word might be a mutation of, itself included. */
export function radicals(w: string): Set<string> {
  const out = new Set([w])
  for (const [a, b] of UNMUTATE) if (w.startsWith(a) && w.length > a.length + 1) out.add(b + w.slice(a.length))
  if (w.length > 2 && w[0] === 'h' && VOWELS.includes(w[1])) out.add(w.slice(1))
  return out
}

/**
 * THE BLEND: half out and about, half the course's own word counts over its
 * seeds and pod lines, mutation-folded the same way (a word counts for the
 * larger of itself and its attested family).
 */
export function blendUsefulness(
  items: readonly WelshItem[],
  outAndAbout: ReadonlyMap<string, number>,
  /** The mutation fold: Welsh's by default, Irish's (irish.ts) for an Irish deck. */
  radicalsOf: (w: string) => Set<string> = radicals,
): Map<string, number> {
  const tok = new Map<string, number>()
  for (const it of items) if (it.kind === 'S' || it.kind === 'P') for (const w of wordsOf(it.cy)) tok.set(w, (tok.get(w) ?? 0) + 1)
  let total = 0
  for (const n of tok.values()) total += n
  const fam = new Map<string, number>()
  for (const [w, n] of tok) for (const r of radicalsOf(w)) if (r === w || tok.has(r)) fam.set(r, (fam.get(r) ?? 0) + 1e6 * n / total)
  const course = new Map<string, number>()
  for (const [w, n] of tok) {
    let best = 1e6 * n / total
    for (const r of radicalsOf(w)) if (r === w || tok.has(r)) best = Math.max(best, fam.get(r) ?? 0)
    course.set(w, best)
  }
  const out = new Map<string, number>()
  for (const w of new Set([...outAndAbout.keys(), ...course.keys()])) {
    out.set(w, STREET_SHARE * (outAndAbout.get(w) ?? 0) + (1 - STREET_SHARE) * (course.get(w) ?? 0))
  }
  return out
}

const rarest = (xs: number[]): number => (xs.length ? Math.max(Math.min(...xs), FLOOR) : FLOOR)

/** The items in the mixed order, each with the chunks it opens at its place (0 = joins quietly). */
export function orderByUsefulness(items: readonly WelshItem[], table: ReadonlyMap<string, number>): { item: WelshItem; load: number }[] {
  const u = (w: string) => table.get(w) ?? FLOOR
  const chunkU = new Map<string, number>()
  const cu = (c: string) => {
    let v = chunkU.get(c)
    if (v === undefined) { v = rarest(c.split(' ').map(u)); chunkU.set(c, v) }
    return v
  }
  const iu = new Map(items.map(it => [it.id, rarest(wordsOf(it.cy).map(u))]))
  const incoming = new Map(items.map((it, i) => [it.id, i]))
  const uniq = new Map(items.map(it => [it.id, [...new Set(it.chunks)]]))
  const known = new Set<string>()
  let left = [...items]
  const order: { item: WelshItem; load: number }[] = []
  while (left.length) {
    const news = new Map(left.map(it => [it.id, uniq.get(it.id)!.filter(c => !known.has(c))]))
    const free = left.filter(it => news.get(it.id)!.length === 0)
    if (free.length) {
      free.sort((a, b) => iu.get(b.id)! - iu.get(a.id)! || incoming.get(a.id)! - incoming.get(b.id)!)
      for (const it of free) order.push({ item: it, load: 0 })
      const gone = new Set(free.map(it => it.id))
      left = left.filter(it => !gone.has(it.id))
      continue
    }
    const m = Math.min(...left.map(it => news.get(it.id)!.length))
    const unlocks = new Map<string, number>()
    for (const it of left) {
      const n = news.get(it.id)!
      if (n.length === 1 && it.kind !== 'L') unlocks.set(n[0], (unlocks.get(n[0]) ?? 0) + UNLOCK * iu.get(it.id)!)
    }
    const gain = (it: WelshItem) => news.get(it.id)!.reduce((a, c) => a + cu(c) + (unlocks.get(c) ?? 0), 0) / m
    let pick: WelshItem | null = null, pg = 0
    for (const it of left) {
      if (news.get(it.id)!.length !== m) continue
      const g = gain(it)
      if (!pick) { pick = it; pg = g; continue }
      // (gain, then the lower kind, then usefulness, then the earlier incoming place) — #145's key.
      const better = g !== pg ? g > pg
        : KIND_RANK[it.kind] !== KIND_RANK[pick.kind] ? KIND_RANK[it.kind] < KIND_RANK[pick.kind]
          : iu.get(it.id)! !== iu.get(pick.id)! ? iu.get(it.id)! > iu.get(pick.id)!
            : incoming.get(it.id)! < incoming.get(pick.id)!
      if (better) { pick = it; pg = g }
    }
    const p = pick!
    order.push({ item: p, load: m })
    for (const c of news.get(p.id)!) known.add(c)
    left = left.filter(it => it !== p)
  }
  return order
}

/**
 * Read an out-and-about word table that carries no family column — Irish's,
 * tools/script-corpus/gle/out/out-and-about.gle.tsv (job #146): rank, word, per
 * million, then source splits. A word scores the larger of itself and its
 * MUTATION family (every form the fold sends to the same dictionary word), as
 * the Welsh table's family column does. Not the lemma column: that merges
 * tá with bhí and beidh, and leaves most street words blank.
 */
export function parseOutAndAboutFolded(tsv: string, fold: (w: string) => string): Map<string, number> {
  const rows: [string, number][] = []
  const family = new Map<string, number>()
  for (const line of tsv.split('\n')) {
    if (!line || line.startsWith('#')) continue
    const f = line.split('\t')
    const pm = Number(f[2])
    rows.push([f[1], pm])
    const r = fold(f[1])
    family.set(r, (family.get(r) ?? 0) + pm)
  }
  return new Map(rows.map(([w, pm]) => [w, Math.max(pm, family.get(fold(w)) ?? 0)]))
}

/** Read an out-and-about word table (tools/script-corpus/cym/out/out-and-about.<variety>.tsv): word → its family score per million. */
export function parseOutAndAbout(tsv: string): Map<string, number> {
  const out = new Map<string, number>()
  for (const line of tsv.split('\n')) {
    if (!line || line.startsWith('#')) continue
    const f = line.split('\t')
    out.set(f[1], Number(f[f.length - 1]))
  }
  return out
}
